import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import { courseProjects } from '../data/courseProjects';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import { 
    faChevronDown, 
    faChevronUp, 
    faArrowUpRightFromSquare, 
    faXmark,
    faMagnifyingGlassPlus
} from '@fortawesome/free-solid-svg-icons';

const CourseProjects = () => {
    const { theme, themes } = useContext(ThemeContext);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    return (
        <div className="w-full max-w-6xl mx-auto mt-20 pt-8 border-t border-white/10 px-4">
            {/* Collapsible Header */}
            <button
                type="button"
                aria-expanded={isOpen}
                aria-controls="course-projects-grid"
                onClick={() => setIsOpen((open) => !open)}
                className="flex w-full items-center justify-between cursor-pointer group py-4 px-5 rounded-2xl text-left transition-all duration-300 hover:bg-white/[0.04]"
            >
                <span>
                    <span role="heading" aria-level="2" className={`text-xl md:text-2xl font-bold tracking-tight flex items-center gap-3 ${themes[theme].text}`}>
                        Archive & Course Projects
                        <span className="text-xs px-2.5 py-0.5 rounded-full border border-white/15 opacity-60 font-mono">
                            {courseProjects.length}
                        </span>
                    </span>
                    <span className={`block text-xs md:text-sm opacity-60 mt-1 ${themes[theme].subText}`}>
                        Foundational academic projects, early Swift & Objective-C projects
                    </span>
                </span>

                <span className="flex items-center gap-2 text-sm opacity-70 group-hover:opacity-100 transition-opacity">
                    <span className="text-xs hidden sm:inline-block font-mono">
                        {isOpen ? 'Collapse' : 'Explore'}
                    </span>
                    <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                </span>
            </button>

            {/* Collapsible Project Grid */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        id="course-projects-grid"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                    >
                        <div className="pt-8 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {courseProjects.map((proj) => (
                                    <div
                                        key={proj.id}
                                        className="group flex flex-col justify-between rounded-2xl overflow-hidden bg-white/[0.03] border border-white/10 hover:border-white/25 hover:bg-white/[0.06] transition-all duration-300"
                                    >
                                        {/* Image Container with Zoom & Click to Preview */}
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`Preview ${proj.title}`}
                                            className="relative h-48 sm:h-52 w-full overflow-hidden bg-black/40 cursor-pointer flex items-center justify-center"
                                            onClick={() => setSelectedImage(proj.image)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    setSelectedImage(proj.image);
                                                }
                                            }}
                                        >
                                            {proj.image2 ? (
                                                <div className="flex w-full h-full gap-1 p-2">
                                                    <img
                                                        src={proj.image}
                                                        alt={proj.title}
                                                        className="w-1/2 h-full object-cover rounded-lg group-hover:scale-[1.03] transition-transform duration-300"
                                                        loading="lazy"
                                                    />
                                                    <img
                                                        src={proj.image2}
                                                        alt={`${proj.title} secondary`}
                                                        className="w-1/2 h-full object-cover rounded-lg group-hover:scale-[1.03] transition-transform duration-300"
                                                        loading="lazy"
                                                    />
                                                </div>
                                            ) : (
                                                <img
                                                    src={proj.image}
                                                    alt={proj.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                    loading="lazy"
                                                />
                                            )}

                                            {/* Hover magnifying hint */}
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white text-xs font-semibold backdrop-blur-[2px]">
                                                <FontAwesomeIcon icon={faMagnifyingGlassPlus} />
                                                <span>Expand</span>
                                            </div>
                                        </div>

                                        {/* Project Meta */}
                                        <div className="p-4 flex flex-col flex-1 justify-between">
                                            <div>
                                                <div className="flex items-start justify-between gap-2">
                                                    <h3 className={`text-base font-bold tracking-tight ${themes[theme].text}`}>
                                                        {proj.title}
                                                    </h3>
                                                    {proj.link1 && (
                                                        <a
                                                            href={proj.link1}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-white/60 hover:text-white p-1 transition-colors"
                                                            title="GitHub Repo"
                                                        >
                                                            <FontAwesomeIcon icon={faGithub} className="text-base" />
                                                        </a>
                                                    )}
                                                </div>

                                                <p className={`text-xs mt-2 opacity-65 leading-relaxed line-clamp-3 ${themes[theme].subText}`}>
                                                    {proj.description}
                                                </p>
                                            </div>

                                            {/* Tech Tag / Archive Pill */}
                                            <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] opacity-40 font-mono">
                                                <span>Course Projects</span>
                                                <span>Swift / Obj-C</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Clean Link to Full Profile */}
                            <div className="mt-10 text-center">
                                <a
                                    href="https://github.com/shivampatel066"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-xs opacity-60 hover:opacity-100 transition-opacity tracking-wide underline underline-offset-4"
                                >
                                    <span>View more repositories on GitHub</span>
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[10px]" />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Lightbox Image Preview Modal */}
            <AnimatePresence>
                {selectedImage && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm cursor-zoom-out"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSelectedImage(null)}
                    >
                        <button
                            type="button"
                            onClick={() => setSelectedImage(null)}
                            className="absolute top-5 right-5 text-white/70 hover:text-white text-xl p-2 z-10"
                            aria-label="Close image preview"
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>

                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="relative max-w-4xl max-h-[85vh] rounded-xl overflow-hidden shadow-2xl border border-white/10"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img
                                src={selectedImage}
                                alt="Project enlarged preview"
                                className="w-full h-full max-h-[85vh] object-contain rounded-xl"
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default CourseProjects;
